import React from "react";
import { useNavigate } from "react-router-dom";
import styles from "./activeCampaignsPanel.module.css";
import { EMAIL_MARKETING_CAMPAIGN_PATH } from "../../../../utils/constants/routes";
import { Button } from "../../../../components/Buttons";

const stateKey = (campaign) => {
  // The backend returns an explicit `unenrolled: true` flag on top of the
  // `state` string. Prefer the flag - when the record is unenrolled the
  // `state` field can still read "unenrolled" or sometimes the last active
  // state, so the boolean is the authoritative signal.
  if (campaign?.unenrolled === true) return "unenrolled";
  const s = (campaign?.state || "").toLowerCase();
  if (s === "unenrolled") return "unenrolled";
  if (s === "running" || s === "active" || s === "sending") return "running";
  if (s === "paused") return "paused";
  if (s === "completed") return "completed";
  if (s === "scheduled" || s === "ready_to_run" || s === "ready to run")
    return "scheduled";
  return "default";
};

// Title-case the raw backend state ("Ready To Run", "ready_to_run", "PAUSED"
// etc.) so the chip mirrors whatever the API sends. Falls back to the
// generic label when the campaign has no state string.
const titleCaseState = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  return raw
    .replace(/[_\-]+/g, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const headerLabelForState = (key, campaign) => {
  // Unenrolled is a derived signal (not always present in `state`), so keep
  // the explicit label for that case.
  if (key === "unenrolled") return "Unenrolled Campaign";
  // Prefer the exact backend `state` so values like "Ready To Run" surface
  // verbatim instead of being collapsed into a generic bucket label.
  const fromApi = titleCaseState(campaign?.state);
  if (fromApi) return fromApi;
  if (key === "paused") return "Paused Campaign";
  if (key === "completed") return "Completed Campaign";
  if (key === "scheduled") return "Scheduled Campaign";
  return "Active Campaign";
};

const themeForState = (key) => {
  switch (key) {
    case "running":
      return {
        card: styles.cardRunning,
        dot: styles.dotRunning,
        label: styles.labelRunning,
      };
    case "paused":
      return {
        card: styles.cardPaused,
        dot: styles.dotPaused,
        label: styles.labelPaused,
      };
    case "completed":
      return {
        card: styles.cardCompleted,
        dot: styles.dotCompleted,
        label: styles.labelCompleted,
      };
    case "scheduled":
      return {
        card: styles.cardScheduled,
        dot: styles.dotScheduled,
        label: styles.labelScheduled,
      };
    case "unenrolled":
      return {
        card: styles.cardUnenrolled,
        dot: styles.dotUnenrolled,
        label: styles.labelUnenrolled,
      };
    default:
      return {
        card: styles.cardDefault,
        dot: styles.dotDefault,
        label: styles.labelDefault,
      };
  }
};

const ActiveCampaignsPanel = ({ loading, campaigns = [], onUnenroll }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className={styles.wrap}>
        <div className={`${styles.card} ${styles.cardDefault}`}>
          <div
            className={styles.skeleton}
            style={{ height: 11, width: 110 }}
          />
          <div
            className={styles.skeleton}
            style={{ height: 14, width: 160 }}
          />
          <div
            className={styles.skeleton}
            style={{ height: 12, width: 140 }}
          />
        </div>
      </div>
    );
  }

  if (!campaigns?.length) return null;

  const goToCampaign = (campaignId) => {
    if (!campaignId) return;
    navigate(`${EMAIL_MARKETING_CAMPAIGN_PATH}/${campaignId}`);
  };

  return (
    <div className={styles.wrap}>
      {campaigns.map((c) => {
        const key = stateKey(c);
        const theme = themeForState(key);
        // Once unenrolled, the contact isn't an active participant anymore -
        // hide the action so they can't try to re-unenroll a dead record.
        //
        // A FINISHED campaign is the same situation: there is nothing left to unenroll from, and
        // pressing it produced a nonsense line - "1 of 8 steps done, Unenrolled at step 2" - on a
        // campaign that had already run its course. Anything terminal hides the action; paused does
        // NOT, because a paused campaign will resume and unenrolling is the only way out of it.
        const CAMPAIGN_OVER = ["unenrolled", "completed", "finished", "ended", "stopped", "interrupted", "cancelled", "canceled"];
        const showUnenroll = !!onUnenroll && !CAMPAIGN_OVER.includes(key);
        return (
          <div key={c.campaign_id} className={`${styles.card} ${theme.card}`}>
            <div className={styles.headerRow}>
              <span className={`${styles.dot} ${theme.dot}`} />
              <span className={`${styles.headerLabel} ${theme.label}`}>
                {headerLabelForState(key, c)}
              </span>
            </div>
            <div
              className={styles.name}
              onClick={() => goToCampaign(c.campaign_id)}
            >
              {c.campaign_name || "Campaign"}
            </div>
            {/*
              Hide the step summary while the campaign is still in pre-run
              states ("Ready To Run", "ready_to_run", "draft", "queued").
              The backend stamps a summary like "Step 1 of 8, Pending" the
              moment the record is created, which is misleading before any
              touch has actually been scheduled.
            */}
            {c.step_summary &&
              !["ready to run", "ready_to_run", "draft", "queued"].includes(
                String(c.state || "").trim().toLowerCase(),
              ) && (
                <div className={styles.summary}>
                  {String(c.step_summary).replace(/\s*[-—–]\s*/g, ", ")}
                </div>
              )}
            {showUnenroll && (
              <div className={styles.actions}>
                <Button
                  label="Unenroll"
                  size="sm36"
                  variant="ghost"
                  onClick={() => onUnenroll(c)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ActiveCampaignsPanel;
